// frontend/src/pages/Market.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { createChart } from "lightweight-charts";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const BRAND_BLUE  = "#1D4ED8";
const RED_DOWN    = "#DC2626";
const GRAY_BORDER = "#E5E7EB";
const TEXT_DARK   = "#1F2937";

// Map our human-readable pair to CoinGecko ID
const PAIRS = [
  { label: "USDT/INR", key: "USDT-INR", id: "tether" },
  { label: "BTC/INR",  key: "BTC-INR",  id: "bitcoin" },
  { label: "ETH/INR",  key: "ETH-INR",  id: "ethereum" },
];

const TIMEFRAMES = [1, 7, 30, 90, 180, 365]; // days

export default function Market() {
  const [selectedPair, setSelectedPair] = useState("USDT-INR");
  const [timeframe, setTimeframe] = useState(7);
  const [chartType, setChartType] = useState("candles"); // "candles" | "area"
  const [showMA7, setShowMA7] = useState(true);
  const [showMA25, setShowMA25] = useState(false);
  const [ohlc, setOhlc] = useState([]); // [{time, open, high, low, close}]
  const [loading, setLoading] = useState(true);

  // track dark mode by watching <html class="dark">
  const [isDark, setIsDark] = useState(
    typeof document !== "undefined" &&
      document.documentElement.classList.contains("dark")
  );

  // Chart refs
  const chartContainerRef = useRef(null);
  const chartRef = useRef(null);
  const priceSeriesRef = useRef(null);
  const ma7Ref = useRef(null);
  const ma25Ref = useRef(null);
  const roRef = useRef(null);

  // ------- Derived helpers -------
  const currentPair = useMemo(
    () => PAIRS.find((p) => p.key === selectedPair),
    [selectedPair]
  );

  const stats = useMemo(() => {
    if (!ohlc.length) return null;
    const opens  = ohlc[0]?.open ?? 0;
    const closes = ohlc[ohlc.length - 1]?.close ?? 0;
    let hi = -Infinity;
    let lo = Infinity;
    for (const c of ohlc) {
      if (c.high > hi) hi = c.high;
      if (c.low < lo) lo = c.low;
    }
    const changeAbs = closes - opens;
    const changePct = opens ? (changeAbs / opens) * 100 : 0;
    return {
      open: opens,
      close: closes,
      high: hi,
      low: lo,
      changeAbs,
      changePct,
      up: changeAbs >= 0,
    };
  }, [ohlc]);

  // ------- Watch for theme toggle (.dark class) -------
  useEffect(() => {
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains("dark"));
    sync();
    const mo = new MutationObserver(sync);
    mo.observe(el, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  // ------- Fetch OHLC on changes -------
  useEffect(() => {
    let aborted = false;
    const ctrl = new AbortController();

    (async () => {
      try {
        setLoading(true);
        setOhlc([]);
        const coinId = currentPair?.id;
        if (!coinId) {
          toast.error("Unsupported pair");
          setLoading(false);
          return;
        }
        const url = `https://api.coingecko.com/api/v3/coins/${coinId}/ohlc?vs_currency=inr&days=${timeframe}`;
        const resp = await fetch(url, { signal: ctrl.signal });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const raw = await resp.json();
        if (aborted) return;

        const formatted = Array.isArray(raw)
          ? raw.map(([ts, open, high, low, close]) => ({
              time: Math.floor(ts / 1000),
              open,
              high,
              low,
              close,
            }))
          : [];
        setOhlc(formatted);
      } catch (err) {
        if (err.name !== "AbortError") {
          console.error("Market: fetchOHLC error", err);
          toast.error("Failed to load chart data");
        }
      } finally {
        if (!aborted) setLoading(false);
      }
    })();

    return () => {
      aborted = true;
      ctrl.abort();
    };
  }, [currentPair?.id, timeframe]);

  // ------- Create chart ONCE when container mounts -------
  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return; // container not in DOM yet

    const initialW = Math.max(250, Math.floor(el.clientWidth || 0));
    const initialH =
      Math.max(220, Math.floor(el.clientHeight || 0)) ||
      (window.innerWidth < 640 ? 280 : 420);

    // light defaults (light theme remains unchanged)
    const chart = createChart(el, {
      width: initialW,
      height: initialH,
      layout: { backgroundColor: "#FFFFFF", textColor: TEXT_DARK },
      grid: {
        vertLines: { color: GRAY_BORDER },
        horzLines: { color: GRAY_BORDER },
      },
      rightPriceScale: { borderColor: GRAY_BORDER },
      timeScale: { borderColor: GRAY_BORDER, timeVisible: true, secondsVisible: false },
      crosshair: { mode: 1 },
    });
    chartRef.current = chart;

    // Safe ResizeObserver (width + height)
    const ro = new ResizeObserver((entries) => {
      if (!entries?.[0]) return;
      if (!chartRef.current || !chartContainerRef.current) return;
      const { width, height } = entries[0].contentRect || {};
      if (width)  chartRef.current.applyOptions({ width: Math.max(250, Math.floor(width)) });
      if (height) chartRef.current.applyOptions({ height: Math.max(220, Math.floor(height)) });
    });
    ro.observe(el);
    roRef.current = ro;

    // Cleanup
    return () => {
      try { roRef.current?.disconnect(); } catch {}
      roRef.current = null;

      try {
        if (priceSeriesRef.current) {
          chartRef.current?.removeSeries(priceSeriesRef.current);
          priceSeriesRef.current = null;
        }
        if (ma7Ref.current) {
          chartRef.current?.removeSeries(ma7Ref.current);
          ma7Ref.current = null;
        }
        if (ma25Ref.current) {
          chartRef.current?.removeSeries(ma25Ref.current);
          ma25Ref.current = null;
        }
      } catch {}
      try { chartRef.current?.remove(); } catch {}
      chartRef.current = null;
    };
  }, []); // mount once

  // ------- Apply chart palette when theme changes -------
  useEffect(() => {
    if (!chartRef.current) return;
    if (isDark) {
      chartRef.current.applyOptions({
        layout: { backgroundColor: "#000000", textColor: "#FFFFFF" },
        grid: {
          vertLines: { color: "#262626" },
          horzLines: { color: "#262626" },
        },
        rightPriceScale: { borderColor: "#262626" },
        timeScale: { borderColor: "#262626" },
      });
    } else {
      chartRef.current.applyOptions({
        layout: { backgroundColor: "#FFFFFF", textColor: TEXT_DARK },
        grid: {
          vertLines: { color: GRAY_BORDER },
          horzLines: { color: GRAY_BORDER },
        },
        rightPriceScale: { borderColor: GRAY_BORDER },
        timeScale: { borderColor: GRAY_BORDER },
      });
    }
  }, [isDark]);

  // ------- Create or switch PRICE series when chartType changes -------
  useEffect(() => {
    if (!chartRef.current) return;

    // Remove old price series (if any)
    if (priceSeriesRef.current) {
      try { chartRef.current.removeSeries(priceSeriesRef.current); } catch {}
      priceSeriesRef.current = null;
    }

    // Add new price series
    if (chartType === "candles") {
      const s = chartRef.current.addCandlestickSeries({
        upColor: BRAND_BLUE,
        downColor: RED_DOWN,
        borderUpColor: BRAND_BLUE,
        borderDownColor: RED_DOWN,
        wickUpColor: BRAND_BLUE,
        wickDownColor: RED_DOWN,
      });
      priceSeriesRef.current = s;
      if (ohlc.length) s.setData(ohlc);
    } else {
      const s = chartRef.current.addAreaSeries({
        lineWidth: 2,
        lineColor: BRAND_BLUE,
        topColor: "rgba(29, 78, 216, 0.20)",
        bottomColor: "rgba(29, 78, 216, 0.00)",
      });
      priceSeriesRef.current = s;
      if (ohlc.length) {
        s.setData(ohlc.map((d) => ({ time: d.time, value: d.close })));
      }
    }

    try { chartRef.current.timeScale().fitContent(); } catch {}
  }, [chartType, ohlc]);

  // ------- Update PRICE series data when ohlc changes -------
  useEffect(() => {
    if (!priceSeriesRef.current) return;
    if (!ohlc || ohlc.length === 0) {
      try { priceSeriesRef.current.setData([]); } catch {}
      return;
    }
    try {
      if (chartType === "candles") {
        priceSeriesRef.current.setData(ohlc);
      } else {
        priceSeriesRef.current.setData(
          ohlc.map((d) => ({ time: d.time, value: d.close }))
        );
      }
      chartRef.current?.timeScale().fitContent();
    } catch {}
  }, [ohlc, chartType]);

  // ------- Overlays (MAs): create/remove & update on toggles/ohlc -------
  useEffect(() => {
    if (!chartRef.current) return;

    // MA 7
    if (showMA7) {
      if (!ma7Ref.current) {
        ma7Ref.current = chartRef.current.addLineSeries({
          color: "#059669", // green-ish
          lineWidth: 1.5,
        });
      }
      const data7 = calcSMA(ohlc, 7).map((d) => ({ time: d.time, value: d.value }));
      try { ma7Ref.current.setData(data7); } catch {}
    } else if (ma7Ref.current) {
      try { chartRef.current.removeSeries(ma7Ref.current); } catch {}
      ma7Ref.current = null;
    }

    // MA 25
    if (showMA25) {
      if (!ma25Ref.current) {
        ma25Ref.current = chartRef.current.addLineSeries({
          color: "#6B7280", // gray-ish
          lineWidth: 1.5,
        });
      }
      const data25 = calcSMA(ohlc, 25).map((d) => ({ time: d.time, value: d.value }));
      try { ma25Ref.current.setData(data25); } catch {}
    } else if (ma25Ref.current) {
      try { chartRef.current.removeSeries(ma25Ref.current); } catch {}
      ma25Ref.current = null;
    }
  }, [showMA7, showMA25, ohlc]);

  return (
    // Keep light theme exactly the same; add dark bg only
    <div className="min-h-screen bg-blue-50 dark:bg-black pb-10">
      {/* Header */}
      <header className="bg-white dark:bg-black shadow-lg border-b-4 border-blue-600">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 md:py-6 flex flex-col gap-1">
          <h1 className="text-2xl sm:text-3xl font-bold text-blue-800 dark:text-white">Market</h1>
          <p className="text-xs sm:text-sm text-blue-600 dark:text-gray-300">
            Interactive candlestick &amp; line charts with quick timeframes and overlays.
          </p>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 mt-5 space-y-6">
        {/* Controls */}
        <section className="bg-white dark:bg-black rounded-2xl shadow-sm border p-3 sm:p-4">
          {/* Pair chips (scrollable on mobile) */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {PAIRS.map((p) => (
              <button
                key={p.key}
                onClick={() => setSelectedPair(p.key)}
                className={`whitespace-nowrap text-xs sm:text-sm px-3 py-1.5 rounded-full border transition ${
                  selectedPair === p.key
                    ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-black dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-900"
                }`}
                type="button"
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Timeframe + Type + Overlays */}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 items-center">
            {/* Timeframes */}
            <div className="flex flex-wrap items-center gap-1.5">
              {TIMEFRAMES.map((d) => (
                <button
                  key={d}
                  onClick={() => setTimeframe(d)}
                  className={`text-xs sm:text-sm px-3 py-1.5 rounded-md border ${
                    timeframe === d
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-black dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-900"
                  }`}
                  type="button"
                >
                  {d === 1 ? "1D" : d === 7 ? "7D" : d === 30 ? "30D" : d + "D"}
                </button>
              ))}
            </div>

            {/* Chart Type */}
            <div className="flex items-center gap-1.5">
              {["candles", "area"].map((t) => (
                <button
                  key={t}
                  onClick={() => setChartType(t)}
                  className={`text-xs sm:text-sm px-3 py-1.5 rounded-md border ${
                    chartType === t
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 dark:bg-black dark:text-gray-200 dark:border-gray-700 dark:hover:bg-gray-900"
                  }`}
                  type="button"
                >
                  {t === "candles" ? "Candles" : "Line"}
                </button>
              ))}
            </div>

            {/* Overlays */}
            <div className="flex items-center gap-3 justify-start md:justify-end">
              <label className="flex items-center gap-1 text-xs sm:text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-blue-600"
                  checked={showMA7}
                  onChange={() => setShowMA7((s) => !s)}
                />
                MA 7
              </label>
              <label className="flex items-center gap-1 text-xs sm:text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-blue-600"
                  checked={showMA25}
                  onChange={() => setShowMA25((s) => !s)}
                />
                MA 25
              </label>
              <button
                type="button"
                onClick={() => setTimeframe((t) => t)} // soft refresh
                className="ml-auto md:ml-0 text-xs sm:text-sm px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50 dark:bg-black dark:hover:bg-gray-900 dark:border-gray-700 dark:text-gray-200"
                title="Refresh"
              >
                Refresh
              </button>
            </div>
          </div>
        </section>

        {/* Chart & stats */}
        <section className="bg-white dark:bg-black rounded-2xl shadow-sm border p-3 sm:p-4">
          {/* Stats strip (compact on mobile) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-3 sm:mb-4">
            <Stat label="Open"  value={stats?.open} />
            <Stat label="Close" value={stats?.close} />
            <Stat label="High"  value={stats?.high} />
            <Stat label="Low"   value={stats?.low} />
            <div className="col-span-2 sm:col-span-4">
              <ChangeBadge abs={stats?.changeAbs} pct={stats?.changePct} up={!!stats?.up} />
            </div>
          </div>

          {/* Always present chart container with overlays for loading/empty */}
          <div className="relative">
            {/* Set an explicit height so the chart can measure on mount */}
            <div ref={chartContainerRef} className="w-full h-[260px] sm:h-[380px]" />

            {loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-blue-600 dark:text-blue-300 text-sm sm:text-base">Loading chart…</p>
              </div>
            )}

            {!loading && ohlc.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <p className="text-red-600 dark:text-red-400 text-sm sm:text-base">
                  No data available. Try another timeframe.
                </p>
              </div>
            )}
          </div>

          {/* Footnote */}
          <p className="mt-3 text-[11px] sm:text-xs text-gray-500 dark:text-gray-400">
            Data from CoinGecko OHLC endpoint. Some timeframes may be rate-limited or smoothed.
            Prices are indicative, not investment advice.
          </p>
        </section>
      </div>
    </div>
  );
}

/* ----------------- Small components ----------------- */

function Stat({ label, value }) {
  if (value == null) {
    return (
      <div className="rounded-lg border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 p-2">
        <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
        <div className="h-4 mt-1 bg-gray-200 dark:bg-gray-700 animate-pulse rounded" />
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-gray-50 dark:bg-gray-900 dark:border-gray-700 p-2">
      <p className="text-[11px] text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white mt-0.5">
        ₹{formatNumber(value)}
      </p>
    </div>
  );
}

function ChangeBadge({ abs = 0, pct = 0, up = true }) {
  const cls = up
    ? "bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300"
    : "bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300";
  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded ${cls}`}>
      <span className="text-xs font-medium">
        {up ? "↑" : "↓"} {up ? "+" : ""}{pct.toFixed(2)}%
      </span>
      <span className="text-[11px] opacity-80">
        ({up ? "+" : "-"}₹{formatNumber(Math.abs(abs))})
      </span>
    </div>
  );
}

/* ----------------- utils ----------------- */

function formatNumber(n) {
  try {
    return Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 });
  } catch {
    return n;
  }
}

function calcSMA(data, len) {
  if (!Array.isArray(data) || data.length === 0) return [];
  const out = [];
  let sum = 0;
  for (let i = 0; i < data.length; i++) {
    sum += data[i].close;
    if (i >= len) sum -= data[i - len].close;
    if (i >= len - 1) {
      out.push({ time: data[i].time, value: sum / len });
    }
  }
  return out;
}
